import { Controller } from "@hotwired/stimulus"

// Notifications toggle. Drives the in-app push preference (server flag) AND
// the OS-level permission, which are independent:
//   • Enable  → set server flag true, then request the iOS permission so the
//               push-token bridge can register a device. The server only
//               accepts the registration once the flag is true.
//   • Disable → set server flag false; the server deletes the device tokens
//               and refuses to re-register on the next page load, so it sticks.
export default class extends Controller {
  static targets = ["button"]
  static values = {
    enabled: Boolean,
    updateUrl: String,
    enabledLabel: { type: String, default: "Enabled" },
    enableLabel: { type: String, default: "Enable" },
    deniedLabel: { type: String, default: "Open iOS Settings" }
  }

  toggle() {
    // Re-entrancy guard: ignore taps while a toggle is in flight so the server
    // flag (and device registration) is never hit twice for one intent.
    if (this.busy) return
    if (this.enabledValue) {
      this.#disable()
    } else {
      this.#enable()
    }
  }

  async #enable() {
    this.busy = true
    this.buttonTarget.disabled = true
    try {
      await this.#setServerFlag(true)

      // Force a clean re-register: drop any cached token so the push-token
      // bridge always re-POSTs after enabling, even if the server device row
      // was removed out-of-band (disable, admin, account switch).
      localStorage.removeItem("floosyo.lastRegisteredPushToken")

      const bridge = this.#pushTokenBridge()
      if (bridge) {
        const result = await bridge.requestPermission()
        if (!result?.granted) {
          // Flag is on, but the OS denied: no token registers, so no pushes
          // are sent. Point the user at iOS Settings to flip OS permission.
          this.buttonTarget.textContent = this.deniedLabelValue
          return
        }
      }

      this.enabledValue = true
      this.buttonTarget.textContent = this.enabledLabelValue
    } finally {
      this.buttonTarget.disabled = false
      this.busy = false
    }
  }

  async #disable() {
    this.busy = true
    this.buttonTarget.disabled = true
    try {
      await this.#setServerFlag(false)

      // The server just deleted the device row. The push-token bridge caches
      // the last-registered token in localStorage and skips re-POSTing an
      // unchanged token — so without clearing it here, a later re-enable would
      // short-circuit and never recreate the device. Clear it so re-enable
      // registers cleanly.
      localStorage.removeItem("floosyo.lastRegisteredPushToken")

      this.enabledValue = false
      this.buttonTarget.textContent = this.enableLabelValue
    } finally {
      this.buttonTarget.disabled = false
      this.busy = false
    }
  }

  // Throws on a non-OK response so callers do NOT flip the button — a failed
  // request must never leave the UI claiming a state the server didn't apply.
  // Renders the server's turbo_stream toast so the change confirms visually,
  // matching the other settings (currency / theme).
  async #setServerFlag(enabled) {
    const token = document.querySelector("meta[name='csrf-token']")?.content
    const response = await fetch(this.updateUrlValue, {
      method: "PATCH",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        "Accept": "text/vnd.turbo-stream.html",
        "X-CSRF-Token": token || ""
      },
      body: JSON.stringify({ enabled })
    })
    if (!response.ok) throw new Error(`notifications update failed: ${response.status}`)

    const html = await response.text()
    if (html) window.Turbo.renderStreamMessage(html)
  }

  #pushTokenBridge() {
    const el = document.querySelector("[data-controller~='bridge--push-token']")
    if (!el) return null
    const ctrl = this.application.getControllerForElementAndIdentifier(el, "bridge--push-token")
    return typeof ctrl?.requestPermission === "function" ? ctrl : null
  }
}
